<template>
  <div v-intersect class="filter-slider" @intersect:enter="onShow">
    <!-- RANGE INPUTS -->
    <div class="filter-slider__fields-box">
      <BaseInput
        v-model.number="inputMin"
        :placeholder="$t('catalog.filters.price_from')"
        type="number"
        :disabled="hasRange"
        @change="handleInputsChange"
      />
      <span class="filter-slider__mdash">&nbsp;&nbsp;</span>
      <BaseInput
        v-model.number="inputMax"
        :placeholder="$t('catalog.filters.price_to')"
        type="number"
        :disabled="hasRange"
        @change="handleInputsChange"
      />
    </div>

    <!-- RANGE SELECT -->
    <div class="filter-slider__range-slider-box">
      <vue-slider
        v-model="range"
        :height="4"
        :dot-size="18"
        :min="filter.min"
        :max="filter.max"
        :silent="true"
        tooltip="none"
        :lazy="true"
        :disabled="hasRange"
        @change="handleSliderChange"
        @dragging="handleSliderDragging"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'

import { PRICE_FILTER_CODE } from '@/constants/common'
import type { CatalogFilter, FilterItemChangePayload } from '~/types/catalog'

type Props = {
  filter: CatalogFilter
  selected: string
}
type Emits = {
  (e: 'on-change' | 'on-show', value: FilterItemChangePayload): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const {
  range,
  inputMin,
  inputMax,
  rangeMin,
  rangeMax,
  hasRange
} = useRangeFilter(props)

const prepareDataForEmit = () => {
  const code = props.filter.code === PRICE_FILTER_CODE
    ? props.filter.uuid
    : props.filter.code

  return {
    filter_code: code,
    filter_name: props.filter.name,
    value: `${rangeMin.value}-${rangeMax.value}`,
    isOne: true
  }
}

const handleSliderDragging = ([from, to]: [number, number]) => {
  inputMin.value = from
  inputMax.value = to
}


const handleSliderChange = ([from, to]: [number, number]) => {
  nextTick(() => {
    inputMin.value = from
    inputMax.value = to
    onChange()
  })
}

const handleInputsChange = () => {
  rangeMin.value = inputMin.value
  rangeMax.value = inputMax.value
  onChange()
}

const onChange = () => {
  emit('on-change', prepareDataForEmit())
}

const onShow = () => {
  emit('on-show',  prepareDataForEmit())
}

const handleFilterChange = (filter: CatalogFilter) => {
  const [from, to] = props.selected?.split('-').map(Number) || []

  rangeMin.value = from !== undefined
    ? Math.max(filter.min, Math.min(rangeMin.value, from))
    : filter.min

  rangeMax.value = to !== undefined
    ? Math.min(filter.max, Math.max(rangeMax.value, to))
    : filter.max
}

watch(() => props.filter, handleFilterChange)
</script>

<style lang="scss" scoped>
.filter-slider {
  flex-direction: column;
  align-items: flex-start;

  &__fields-box {
    margin-bottom: 1.6rem;
  }

  &__range-slider-box {
    position: relative;
    height: 2.2rem;
    margin-bottom: 2rem;
  }

  &__mdash {
    padding: 0 0.4rem;
    font-size: $h6-font-size;
  }

  &__fields-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  //&__in-stock-box {
  //  margin-top: 1.6rem;
  //  margin-bottom: -0.4rem;
  //  height: 2rem;
  //  line-height: 2rem;
  //}
  &__loader {
    position: absolute;
    top: 0;
    left: -1rem;
    right: -1rem;
    bottom: 0;
    background-color: rgba($white-color, 0.5);
    transition: 0.2s;
  }
}
</style>
